import { getCommonBoundingBox } from "./bounds";
import { newElementWith } from "./mutateElement";
import { getMaximumGroups } from "./groups";
import type { ElementsMap, ExcalidrawElement } from "./types";

export interface Spacing {
  axis: "x" | "y";
  value: number;
}

export const applySpacingToElements = (
  selectedElements: ExcalidrawElement[],
  elementsMap: ElementsMap,
  spacing: Spacing,
): ExcalidrawElement[] => {
  if (selectedElements.length < 2) {
    return selectedElements;
  }

  const [start, mid, extent] =
    spacing.axis === "x"
      ? (["minX", "midX", "width"] as const)
      : (["minY", "midY", "height"] as const);

  const groups = getMaximumGroups(selectedElements, elementsMap)
    .map((group) => [group, getCommonBoundingBox(group)] as const)
    .sort((a, b) => a[1][mid] - b[1][mid]);

  if (groups.length < 2) {
    return selectedElements;
  }

  // Start from the first group's position (don't move the first group)
  let currentPosition = groups[0][1][start] + groups[0][1][extent];

  return groups.flatMap(([group, box], index) => {
    const translation = {
      x: 0,
      y: 0,
    };

    // Don't move the first group
    if (index > 0) {
      // Add spacing and then position the current group
      currentPosition += spacing.value;
      translation[spacing.axis] = currentPosition - box[start];
      // Update position for next iteration
      currentPosition = currentPosition + box[extent];
    }

    return group.map((element) =>
      newElementWith(element, {
        x: element.x + translation.x,
        y: element.y + translation.y,
      }),
    );
  });
};

export const getSpacingBetweenElements = (
  selectedElements: ExcalidrawElement[],
  elementsMap: ElementsMap,
  axis: "x" | "y",
): number => {
  if (selectedElements.length < 2) {
    return 0;
  }

  const [start, mid, extent] =
    axis === "x"
      ? (["minX", "midX", "width"] as const)
      : (["minY", "midY", "height"] as const);

  const groups = getMaximumGroups(selectedElements, elementsMap)
    .map((group) => [group, getCommonBoundingBox(group)] as const)
    .sort((a, b) => a[1][mid] - b[1][mid]);

  if (groups.length < 2) {
    return 0;
  }

  // Calculate average spacing between consecutive groups
  let totalSpacing = 0;
  let spacingCount = 0;

  for (let i = 0; i < groups.length - 1; i++) {
    const currentGroup = groups[i][1];
    const nextGroup = groups[i + 1][1];
    const spacing = nextGroup[start] - (currentGroup[start] + currentGroup[extent]);
    if (spacing >= 0) {
      totalSpacing += spacing;
      spacingCount++;
    }
  }

  return spacingCount > 0 ? Math.round(totalSpacing / spacingCount) : 0;
}; 